/**
 * Keys that resume a guest conversation.
 *
 * The `start` frame of a guest's new conversation carries `guest_key`, and
 * every later request about that conversation sends it back as `X-Guest-Key`;
 * `new` asks for a key. The backend stores the conversation under a partition
 * derived from the key, so only a request carrying it reaches the conversation.
 * The backend ignores the header for signed-in users, so it is sent on every
 * chat request: guest status on the client can disagree with the backend's (a
 * stale session reaches it as a guest).
 *
 * Keys live in `sessionStorage` beside the guest chat
 * (`utils/guestChatStorage.ts`), so a key survives an in-session refresh as the
 * restored chat does and goes when sign-out clears that storage. Storage is the
 * source of truth; memory is used only where storage is unavailable, and a
 * conversation whose key is gone simply starts over. The mobile app
 * (`src/services/guestKey.ts`) keeps keys in memory, as it keeps its guest chat.
 */

export const GUEST_KEY_HEADER = 'X-Guest-Key';
export const REQUEST_NEW_KEY = 'new';

const STORAGE_KEY = 'votemate_guest_keys';

// Only for when sessionStorage is unavailable (blocked, private mode, quota).
const fallback = new Map<string, string>();

function readStored(): Record<string, string> | null {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '{}');
    const keys: Record<string, string> = {};
    if (parsed && typeof parsed === 'object') {
      for (const [id, key] of Object.entries(parsed)) {
        if (typeof key === 'string' && key) keys[id] = key;
      }
    }
    return keys;
  } catch {
    return null;
  }
}

/** Store the key a `start` frame issued for its conversation. */
export function rememberGuestKey(conversationId: unknown, key: unknown): void {
  if (typeof conversationId !== 'string' || !conversationId || typeof key !== 'string' || !key) {
    return;
  }
  const stored = readStored();
  if (stored) {
    stored[conversationId] = key;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      return;
    } catch {
      // Full or blocked: keep it in memory for this page.
    }
  }
  fallback.set(conversationId, key);
}

/** The header for a request about `conversationId`: its key, or `new` when none is held. */
export function guestKeyHeaders(conversationId?: string | null): Record<string, string> {
  const key = conversationId
    ? (readStored()?.[conversationId] ?? fallback.get(conversationId))
    : undefined;
  return { [GUEST_KEY_HEADER]: key || REQUEST_NEW_KEY };
}

/** Forget every key, in storage and in memory. */
export function clearGuestKeys(): void {
  fallback.clear();
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored to clear.
  }
}
